import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clear_auth_error, select_auth_loading, select_auth_error, select_is_authenticated } from '../store/auth_slice';
import type { AppDispatch } from '../store';

export const Login = () => {
  const [username, set_username] = useState('');
  const [password, set_password] = useState('');
  const [show_password, set_show_password] = useState(false);
  const [search_params] = useSearchParams();
  
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const is_loading = useSelector(select_auth_loading);
  const error_message = useSelector(select_auth_error);
  const is_authenticated = useSelector(select_is_authenticated);

  const reset_success = search_params.get('reset') === 'success';

  useEffect(() => {
    if (is_authenticated) {
      navigate('/dashboard');
    }

    return () => {
      dispatch(clear_auth_error());
    };
  }, [is_authenticated, navigate, dispatch]);

  const handle_submit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(login({ username, password })).unwrap();
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-off-white via-white to-lime-green/5 flex items-center justify-center p-4"
      style={{
        backgroundImage: `
          linear-gradient(rgba(26, 61, 46, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(26, 61, 46, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '4px 4px'
      }}
    >
      <div className="w-full max-w-md">
        {/* Login Form Card */}
        <div className="bg-white rounded-lg border border-light-gray p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-forest-green mb-1.5 tracking-tight">Aqua Pura</h1>
            {/* <p className="text-medium-gray text-sm font-medium">Secure Access Portal</p> */}
          </div>

          <form onSubmit={handle_submit} className="space-y-5">
            {/* Success Message */}
            {reset_success && (
              <div className="bg-success/8 border border-success/25 text-success px-3.5 py-3 rounded-md text-sm font-medium flex items-start gap-2.5">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Password reset successful! Please login with your new password.</span>
              </div>
            )}

            {/* Error Message */}
            {error_message && (
              <div className="bg-error/8 border border-error/25 text-error px-3.5 py-3 rounded-md text-sm font-medium flex items-start gap-2.5">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error_message}</span>
              </div>
            )}

            {/* Username Input */}
            <div>
              <label htmlFor="username" className="block text-xs font-semibold text-charcoal mb-2 uppercase tracking-wide">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-medium-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => set_username(e.target.value)}
                  required
                  className="w-full pl-10 pr-3.5 py-2.5 border border-light-gray rounded-md focus:ring-1 focus:ring-forest-green focus:border-forest-green outline-none transition-colors bg-white text-sm"
                  placeholder="Enter your username"
                  disabled={is_loading}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-charcoal mb-2 uppercase tracking-wide">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-medium-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  type={show_password ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => set_password(e.target.value)}
                  required
                  className="w-full pl-10 pr-10 py-2.5 border border-light-gray rounded-md focus:ring-1 focus:ring-forest-green focus:border-forest-green outline-none transition-colors bg-white text-sm"
                  placeholder="Enter your password"
                  disabled={is_loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => set_show_password(!show_password)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-medium-gray hover:text-charcoal transition-colors p-0.5"
                  tabIndex={-1}
                  aria-label={show_password ? 'Hide password' : 'Show password'}
                >
                  {show_password ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 text-forest-green border-light-gray rounded focus:ring-1 focus:ring-forest-green cursor-pointer"
                />
                <span className="ml-2 text-xs text-medium-gray group-hover:text-charcoal transition-colors font-medium">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-forest-green hover:text-dark-forest font-semibold transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={is_loading}
              className="w-full bg-forest-green text-white py-2.5 rounded-md hover:bg-dark-forest active:bg-forest-green transition-colors duration-150 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-6"
            >
              {is_loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2.5 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-7 pt-6 border-t border-light-gray text-center">
            <p className="text-xs text-medium-gray">
              Don't have an account?{' '}
              <span className="text-forest-green font-semibold cursor-pointer hover:text-dark-forest transition-colors">
                Contact your administrator
              </span>
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-5 text-xs text-medium-gray">
          © {new Date().getFullYear()} Water Management System. All rights reserved.
        </div>
        <div className="text-center mt-2 text-xs text-medium-gray">
          Powered by{' '}
          <a 
            href="https://www.binbyte.co.ke" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-forest-green hover:text-dark-forest font-semibold transition-colors"
          >
            BinByte
          </a>
        </div>
      </div>
    </div>
  );
};